import WRAP from "WRAP-JS";
import { WX_ID_WNI_OCEAN_CURRENT } from "../../../store/WeatherContentsState";
import { _orderBy, formatBaseTime, formatValidTime } from "../../../utils/FPEUtils";
import TimeController from "../helper/TimeController";
import {
  configCurrentLayerArrow,
  configCurrentLayerGradation,
  configCurrentLayerGrid,
  configCurrentLayerStream,
  configOCBaseDh,
  configOCSpeedDirectionConverterDh
} from "./config/ConfigWniOceanCurrent";
import {
  COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER,
  COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD,
  COMMUNICATE_ERROR_MESSAGE_WNI_OCEAN_CURRENT
} from "../../../constants/ErrorMessage";
import { nearValidTime } from "../helper/MapUtil";
import MissingDataObserver from "../helper/MissingDataObserver";

const MISSING_THRESHOLD = {
  [WX_ID_WNI_OCEAN_CURRENT]: 172800 // 48h
}

class WniOceanCurrent {
  static NAME = WX_ID_WNI_OCEAN_CURRENT;
  static ARROW_NAME = `${WniOceanCurrent.NAME}_ARROW`;
  static STREAM_NAME = `${WniOceanCurrent.NAME}_STREAM`;
  static GRADATION_NAME = `${WniOceanCurrent.NAME}_GRADATION`;
  static GRID_NAME = `${WniOceanCurrent.NAME}_GRID`;
  static CONTENTS_KEYS = [
    WniOceanCurrent.ARROW_NAME,
    WniOceanCurrent.STREAM_NAME,
    WniOceanCurrent.GRADATION_NAME,
    WniOceanCurrent.GRID_NAME,
  ];
  static CONTENTS_VISIBLE_STATE_DEFAULT = {
    [WniOceanCurrent.ARROW_NAME]: true,
    [WniOceanCurrent.STREAM_NAME]: true,
    [WniOceanCurrent.GRADATION_NAME]: true,
    [WniOceanCurrent.GRID_NAME]: true,
  };

  contentsVisibleState = {...WniOceanCurrent.CONTENTS_VISIBLE_STATE_DEFAULT};
  dh;
  dhSpdDirConvController;
  dhAutoUpdate = true;
  ready = false;
  layerArrow;
  layerStream;
  layerGradation;
  layerGrid;

  etd;
  elapsed = 0;
  baseTime = '---';
  validTime = '---';
  level = '0';
  dispatcher;
  visible;
  isDark;

  contentsVisibleState = {...WniOceanCurrent.CONTENTS_VISIBLE_STATE_DEFAULT};
  //WNI_OCEAN_CURRENTタイムコントローラー
  timeController;
  //タイムコントローラーのリスト
  timeList;
  //自動更新フラグ
  autoRefresh = true;
  errorMessageInfo = {};

  constructor(isDark) {
    this.isDark = isDark;
    this.timeController = new TimeController(WX_ID_WNI_OCEAN_CURRENT, {
      label: 'WNI Ocean Current',
      getTimeList: () => {
        if (this.timeList) {
          return this.timeList;
        }
        this._updateTimeList();
        return this.timeList;
      },
      updated: () => {
        this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
      },
      changeAutoRefresh: (autoFlg) => {
        this.setAutoRefresh(autoFlg);
      }
    });

    this.layerArrow = new WRAP.Geo.Layer(WniOceanCurrent.ARROW_NAME);
    this.layerStream = new WRAP.Geo.Layer(WniOceanCurrent.STREAM_NAME);
    this.layerGradation = new WRAP.Geo.Layer(WniOceanCurrent.GRADATION_NAME);
    this.layerGrid = new WRAP.Geo.Layer(WniOceanCurrent.GRID_NAME);

    this.layerArrow.setVisible(false);
    this.layerStream.setVisible(false);
    this.layerGradation.setVisible(false);
    this.layerGrid.setVisible(false);

    this.layerArrow.disableInteraction(true);
    this.layerStream.disableInteraction(true);
    this.layerGradation.disableInteraction(true);
    this.layerGrid.disableInteraction(true);
  }

  setDispatcher(dispatcher) {
    this.dispatcher = dispatcher;
    this.missingDataObserver = new MissingDataObserver(this.dispatcher, MISSING_THRESHOLD, this);
  }

  async initialize() {
    try {
      await this.initData();
    } finally {
      this.initLayer();
      this.initTimeController();
      this.ready = true;
    }
  }

  initData() {
    return new Promise((resolve, reject) => {
      this.dh = new WRAP.Data(configOCBaseDh);

      this.missingDataObserver.monitoring(WX_ID_WNI_OCEAN_CURRENT, this.dh, () => ({
        //複数のレイヤーがあるので、ここではvisibleを返すLayerの代わりを設定
        visible: () => this.visible
      }), {
        makeTimelist: (_, ref) => {
          if (!ref) return [];
          return ref.timelist.map(t => t.basetime);
        }
      });

      this.dh.updated('timelist', (ref) => {
        resolve();
        this._selectBaseValidTime(ref);
      });
      this.dh.updated('basetime', () => {
        if (this.errorMessageInfo.basetime) {
          this.dispatcher({type: 'CLEAR_ERROR', payload: this.errorMessageInfo.basetime});
          this.errorMessageInfo.basetime = null;
        }
      }, () => {
        this.errorMessageInfo.basetime = this.dispatcher({type: 'SHOW_ERROR', payload: {
          message: COMMUNICATE_ERROR_MESSAGE_WNI_OCEAN_CURRENT,
          replacement: {data: "basetime"},
          additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
        }});
        reject();
      });
      this.dh.updated('validtime', () => {
        if (this.errorMessageInfo.validtime) {
          this.dispatcher({type: 'CLEAR_ERROR', payload: this.errorMessageInfo.validtime});
          this.errorMessageInfo.validtime = null;
        }
      }, () => {
        this.errorMessageInfo.validtime = this.dispatcher({type: 'SHOW_ERROR', payload: {
          message: COMMUNICATE_ERROR_MESSAGE_WNI_OCEAN_CURRENT,
          replacement: {data: "validtime"},
          additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
        }});
        reject();
      });
      this.dh.updated('region', null, () => {
        this.dispatcher({type: 'SHOW_ERROR', payload: {
          message: COMMUNICATE_ERROR_MESSAGE_WNI_OCEAN_CURRENT,
          replacement: {data: "region"},
          additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD
        }});
        reject();
      });
      this.dh.updated('level', null, () => {
        this.dispatcher({type: 'SHOW_ERROR', payload: {
          message: COMMUNICATE_ERROR_MESSAGE_WNI_OCEAN_CURRENT,
          replacement: {data: "level"},
          additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD
        }});
        reject();
      });
      this.dh.updated('element', null, () => {
        this.dispatcher({type: 'SHOW_ERROR', payload: {
          message: COMMUNICATE_ERROR_MESSAGE_WNI_OCEAN_CURRENT,
          replacement: {data: "element"},
          additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD
        }});
        reject();
      });
      this.dh.updated('updatetime', null, () => {
        this.dispatcher({type: 'SHOW_ERROR', payload: {
          message: COMMUNICATE_ERROR_MESSAGE_WNI_OCEAN_CURRENT,
          replacement: {data: "updatetime"},
          additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD
        }});
        reject();
      });

      this.dhSpdDirConvController = new WRAP.Data(configOCSpeedDirectionConverterDh);

      this.dh.validate((displayTime, data, content) => {
        return this._validateTime(data, displayTime, content);
      });
      this.dhSpdDirConvController.validate((displayTime, data, content) => {
        return this._validateTime(data, displayTime, content);
      });
    });
  }

  initLayer() {
    this.layerArrow.configure(this.dh, configCurrentLayerArrow);
    this.layerStream.configure(this.dh, configCurrentLayerStream);
    this.layerGradation.configure(this.dhSpdDirConvController, configCurrentLayerGradation);
    this.layerGrid.configure(this.dhSpdDirConvController, configCurrentLayerGrid);

    this.applyMapColor(null, this.isDark);

    this.layerArrow.setVisible(false);
    this.layerStream.setVisible(false);
    this.layerGradation.setVisible(false);
    this.layerGrid.setVisible(false);
  }

  initTimeController() {
    this._allLayers().forEach(layer => {
      this.timeController.addLayer(layer);
    });
    if (this.visible) {
      this.timeController.setDisplayTime(this.validTime);
    }
  }

  async drawLayer(visible) {
    this.visible = visible;
    if (!this.ready && !this.dh) {
      await this.initialize();
    }
    this.setVisible(this.visible);
  }

  getLayer() {
    return this._allLayers();
  }

  setVisible(visible, contents) {

    if (contents) {
      this.contentsVisibleState[contents] = visible;
    }

    if (!this.ready) return;

    let target;
    switch (contents) {
      case WniOceanCurrent.ARROW_NAME:
        target = [this.layerArrow];
        break;
      case WniOceanCurrent.STREAM_NAME:
        target = [this.layerStream];
        break;
      case WniOceanCurrent.GRADATION_NAME:
        target = [this.layerGradation];
        break;
      case WniOceanCurrent.GRID_NAME:
        target = [this.layerGrid];
        break;
      default:
        target = this._allLayers();
    }

    if (visible && this.visible) {
      target.forEach(layer => {
        const layerName = layer.name();
        if (this.contentsVisibleState[layerName]) {
          layer.show();
        }
      });
    } else {
      target.forEach(layer => {layer.hide()})
    }

    if (contents === undefined) {
      const enabled = visible && this.autoRefresh;
      if (this.dhAutoUpdate !== enabled) {
        this.dh.setAutoUpdate('timelist', enabled);
        this.dhSpdDirConvController.setAutoUpdate('timelist', enabled);
        this.dhAutoUpdate = enabled;
      }
      this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
    }
  }

  getTimeInformation() {
    return {
      id: WniOceanCurrent.NAME,
      label: 'WNI Ocean Current',
      baseTime: {
        id: WniOceanCurrent.NAME,
        time: this.baseTime
      },
      validTime: this.validTime,
      baseTimeFormatter: (time) => formatBaseTime(time.time),
      validTimeFormatter: formatValidTime
    };
  }

  getLegend() {
    return {
      id: WniOceanCurrent.NAME,
      label: 'WNI Ocean Current',
    };
  }

  getTimeController() {
    return this.timeController;
  }

  getContentsVisibleState() {
    return this.contentsVisibleState;
  }

  resetContentsVisibleState() {
    this.contentsVisibleState = {...WniOceanCurrent.CONTENTS_VISIBLE_STATE_DEFAULT};
  }

  applyElapsed(elapsed) {
    this.elapsed = elapsed;

    if (!this.ready) return;

    this._selectBaseValidTime(this.dh.retained.timelist);
  }

  applyPlanETD(etd) {
    this.etd = etd;

    if (!this.ready) return;

    this._selectBaseValidTime(this.dh.retained.timelist);
  }

  setAutoRefresh(autoFlg) {
    if (autoFlg === this.autoRefresh) {
      return;
    }

    this.autoRefresh = autoFlg;

    if (!this.ready) {
      return;
    }
    //コンテンツの自動更新を切り替え
    this.dh.setAutoUpdate('timelist', this.autoRefresh);
    this.dhSpdDirConvController.setAutoUpdate('timelist', this.autoRefresh);
    this.dhAutoUpdate = this.autoRefresh;
  }
    

  getDataType() {
    return WniOceanCurrent.NAME;
  }

  _selectBaseValidTime(ref) {
    let timeList = ref;
    if (!timeList) return;
    timeList = timeList.timelist;

    let targetTime;
    if (!this.etd) {
      //プラン未選択時
      if (!this.autoRefresh) {
        //タイムコントローラ操作時にレーダーを追加した場合、タイムコントローラの選択時制
        targetTime = this.timeController.getCurrentTime();
      } else {
        targetTime = Date.now();
      }
    } else {
      //プラン選択時
      targetTime = this.etd.getTime() + this.elapsed * 1000;
    }

    //最新のbasetimeを取得
    const latestBasetime = _orderBy(timeList, ['basetime'], ['desc'])[0];

    const vt = nearValidTime(latestBasetime.validtime, targetTime);

    if ((vt && this.validTime !== vt) || this.baseTime !== latestBasetime.basetime) {
      this.baseTime = latestBasetime.basetime;
      this.validTime = vt;
      this._updateDataset();
    }
    this._updateTimeList();
  }

  /**
   * タイムコントロールモード時のvalid選択
   */
  _validateTime(data, displayTime, content) {
    if (!data.retained.timelist) {
      return false;
    }
    //最新のbasetimeを取得
    const latestBasetime = _orderBy(data.retained.timelist.timelist, ['basetime'], ['desc'])[0];

    const vt = nearValidTime(latestBasetime.validtime, displayTime);
    if (vt) {
      this.validTime = content.validtime = vt;
      return true;
    }
    return false
  }

  _updateDataset() {
    if (this.baseTime !== '---' && this.validTime !== '---') {
      this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});

      this.layerArrow.set({
        element: ['UCRRNT', 'VCRRNT'],
        level: this.level,
        basetime: this.baseTime,
        validtime: this.validTime,
        use: 'map'
      });

      this.layerStream.set({
        element: ['UCRRNT', 'VCRRNT'],
        level: this.level,
        basetime: this.baseTime,
        validtime: this.validTime,
        use: 'map'
      });

      this.layerGradation.set({
        element: 'Speed',
        level: this.level,
        basetime: this.baseTime,
        validtime: this.validTime,
        use: 'map'
      });

      this.layerGrid.set({
        element: 'Speed',
        level: this.level,
        basetime: this.baseTime,
        validtime: this.validTime,
        use: 'map'
      });
    }
  }

  _allLayers() {
    return [
      this.layerGradation,
      this.layerStream,
      this.layerArrow,
      this.layerGrid,
    ];
  }

  applyMapColor(mapColor, isDark) {
    const defaultStyle = "default";
    this.layerArrow.setStyle(defaultStyle);
    this.layerStream.setStyle(defaultStyle);
    this.layerGradation.setStyle(defaultStyle);
    this.isDark = isDark;
    const style = this.isDark ? "dark" : defaultStyle;
    this.layerGrid.setStyle(style);
  }

  _updateTimeList() {
    let timelist = this.dh?.retained?.timelist?.timelist;
    if (!timelist) {
      return;
    }

    //最新のbasetimeのvalidtimeを取得
    const latestBasetime = _orderBy(timelist, ['basetime'], ['desc'])[0];
    if (!latestBasetime.validtime) {
      return;
    }
    this.timeList = Array.from(latestBasetime.validtime).sort();

    if (this.autoRefresh) {
      this.timeController.setCurrentTime(this.validTime);
    }
    this.timeController.adjustCurrentTime();
    this.timeController.invalidate();
  }
}

export default WniOceanCurrent;
